import type { OnDestroy, WritableSignal } from '@angular/core';
import { computed, Injectable, signal } from '@angular/core';

import { ACTIVITY_BROADCAST_KEY } from './session-timeout.config';

export type SessionSyncEvent =
  | { type: 'ACTIVITY'; timestamp: number }
  | { type: 'LOGOUT'; isRedirectedFromIdp: boolean }
  | { type: 'HOME'; shouldRedirect: boolean };

export type SignalSetter =
  | { type: 'IDPREDIRECT'; isRedirectedFromIdp: boolean }
  | { type: 'HOME'; shouldRedirect: boolean };
/**
 * Synchronize user activity timestamps across browser tabs so active work in one tab prevents premature warnings in another.
 * @see https://dev.azure.com/KHTechnologySolutions/Decision%20Support%20Tool/_workitems/edit/5406/
 */
@Injectable({ providedIn: 'root' })
export class SessionSyncService implements OnDestroy {
  public readonly lastActivityMs: WritableSignal<number> = signal(Date.now());
  public readonly isRedirectedFromIdp = computed(() =>
    this.#IdpRedirectSignal(),
  );
  public readonly goToHomeSync = computed(() => this.#goToHomeSyncSignal());

  readonly #goToHomeSyncSignal: WritableSignal<boolean> = signal(false);
  readonly #IdpRedirectSignal: WritableSignal<boolean> = signal(false);
  readonly #sessionSyncBroadcaster = new BroadcastChannel(
    ACTIVITY_BROADCAST_KEY,
  );
  readonly #sessionSyncReceiver = new BroadcastChannel(ACTIVITY_BROADCAST_KEY);
  constructor() {
    this.#sessionSyncReceiver.onmessage = (
      event: MessageEvent<SessionSyncEvent>,
    ) => {
      this.#handleMessages(event.data);
    };
  }

  ngOnDestroy(): void {
    this.#sessionSyncReceiver.close();
    this.#sessionSyncBroadcaster.close();
  }

  broadcastMessage(message: SessionSyncEvent): void {
    this.#sessionSyncBroadcaster.postMessage(message);
  }

  #handleMessages(message: SessionSyncEvent): void {
    switch (message.type) {
      case 'ACTIVITY': {
        const incomingActivityMs = message.timestamp;
        if (!incomingActivityMs) return;
        if (incomingActivityMs > this.lastActivityMs()) {
          this.lastActivityMs.set(incomingActivityMs);
        }
        break;
      }
      case 'LOGOUT':
        this.#IdpRedirectSignal.set(message.isRedirectedFromIdp);
        break;
      case 'HOME':
        this.#goToHomeSyncSignal.set(message.shouldRedirect);
        break;
      default:
        break;
    }
  }
}
